import type { LocationQuery, LocationQueryValue } from 'vue-router';

export const PEDIDOS_LIST_QUERY_KEY = 'pedidos:listQuery';

export type SelectOption = {
  name: string;
  value: string;
};

export type OrdersListFilters = {
  search_term: string;
  partner_id: number | null;
  partner_name: string;
  payment_method: string;
  status: string;
  seller: number | null;
  seller_name: string;
  inverter_type: string;
  sort: string;
  page: number;
};

export type BudgetsListFilters = {
  code: number | null;
  partner_id: number | null;
  partner_name: string;
  seller_id: number | null;
  seller_name: string;
  status: string;
  inverter_type: string;
  sort: string;
  page: number;
};

const DEFAULT_SORT = 'created_at:desc';
const DEFAULT_PAGE = 1;

function queryValue(
  query: LocationQuery,
  key: string,
): string {
  const value = query[key];
  if (Array.isArray(value)) {
    return String(value[0] ?? '');
  }
  return String((value as LocationQueryValue) ?? '');
}

function parseNumber(value: string): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function serializeQuery(filters: Record<string, string | number | null | undefined>): Record<string, string> {
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === null || value === undefined || value === '') continue;
    if (key === 'sort' && value === DEFAULT_SORT) continue;
    if (key === 'page' && Number(value) === DEFAULT_PAGE) continue;
    query[key] = String(value);
  }
  return query;
}

function serializeCurrentRouteQuery(query: LocationQuery): string {
  const entries = Object.entries(query)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => [key, Array.isArray(value) ? String(value[0] ?? '') : String(value)])
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(Object.fromEntries(entries));
}

export function parseOrdersQuery(query: LocationQuery): OrdersListFilters {
  return {
    search_term: queryValue(query, 'search_term'),
    partner_id: parseNumber(queryValue(query, 'partner_id')),
    partner_name: queryValue(query, 'partner_name'),
    payment_method: queryValue(query, 'payment_method'),
    status: queryValue(query, 'status'),
    seller: parseNumber(queryValue(query, 'seller')),
    seller_name: queryValue(query, 'seller_name'),
    inverter_type: queryValue(query, 'inverter_type'),
    sort: queryValue(query, 'sort') || DEFAULT_SORT,
    page: parseNumber(queryValue(query, 'page')) || DEFAULT_PAGE,
  };
}

export function parseBudgetsQuery(query: LocationQuery): BudgetsListFilters {
  return {
    code: parseNumber(queryValue(query, 'code')),
    partner_id: parseNumber(queryValue(query, 'partner_id')),
    partner_name: queryValue(query, 'partner_name'),
    seller_id: parseNumber(queryValue(query, 'seller_id')),
    seller_name: queryValue(query, 'seller_name'),
    status: queryValue(query, 'status'),
    inverter_type: queryValue(query, 'inverter_type'),
    sort: queryValue(query, 'sort') || DEFAULT_SORT,
    page: parseNumber(queryValue(query, 'page')) || DEFAULT_PAGE,
  };
}

export function ordersFiltersToQuery(filters: OrdersListFilters): Record<string, string> {
  return serializeQuery({
    search_term: filters.search_term,
    partner_id: filters.partner_id,
    partner_name: filters.partner_name,
    payment_method: filters.payment_method,
    status: filters.status,
    seller: filters.seller,
    seller_name: filters.seller_name,
    inverter_type: filters.inverter_type,
    sort: filters.sort,
    page: filters.page,
  });
}

export function budgetsFiltersToQuery(filters: BudgetsListFilters): Record<string, string> {
  return serializeQuery({
    code: filters.code,
    partner_id: filters.partner_id,
    partner_name: filters.partner_name,
    seller_id: filters.seller_id,
    seller_name: filters.seller_name,
    status: filters.status,
    inverter_type: filters.inverter_type,
    sort: filters.sort,
    page: filters.page,
  });
}

export function findSortOption(
  options: SelectOption[],
  value: string,
  fallback: SelectOption,
): SelectOption {
  return options.find(option => option.value === value) || fallback;
}

export function toSelectOption(
  id: number | null,
  name: string,
): SelectOption {
  if (id == null) {
    return { name: 'Selecione', value: '' };
  }
  return {
    name: name || String(id),
    value: String(id),
  };
}

export function useListQueryFilters() {
  const route = useRoute();
  const router = useRouter();

  function syncToRoute(filters: Record<string, string>) {
    const next = serializeCurrentRouteQuery(filters);
    const current = serializeCurrentRouteQuery(route.query);
    if (next === current) return;
    router.replace({ query: filters });
  }

  function savePedidosListPath() {
    if (import.meta.client) {
      sessionStorage.setItem(PEDIDOS_LIST_QUERY_KEY, route.fullPath);
    }
  }

  function getPedidosListPath(): string {
    if (!import.meta.client) return '/pedidos';
    return sessionStorage.getItem(PEDIDOS_LIST_QUERY_KEY) || '/pedidos';
  }

  return {
    syncToRoute,
    savePedidosListPath,
    getPedidosListPath,
  };
}
